import {
  Dialog,
  DialogActions,
  DialogContent
} from '@mui/material';
import { getCompanyConfigOld } from 'lib/api/company';
import { createCredit } from 'lib/api/creditRequest';
import { getExecutivesAvailableForFactoring } from 'lib/api/factoring';
import ClientInfo from 'lib/common/ClientInfo';
import CompanyBusinessSearcher from 'lib/common/CompanyBusinessSearcher';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import CustomDialogTitle from 'lib/designSystem/DialogTitle';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import CreditSimulator from './components/Simulator';

interface AmortizationRow {
  month: number;
  paymentDate: string;
  balance: number;
  amount: number;
  interest: number;
  principal: number;
  operationCost: number;
}

export const validateCreditRequestForm = (
  creditDetails: any,
  canRateBeZero: boolean | undefined,
  canArrearRateBeZero: boolean | undefined,
) => {
  if (creditDetails.amount <= 0) {
    alert('El monto del crédito no puede ser menor o igual a 0');
    return false;
  }
  if (creditDetails.months <= 0) {
    alert('El número de cuotas no puede ser menor o igual a 0');
    return false;
  }
  if (Number(creditDetails.rate) <= 0 && !canRateBeZero) {
    alert('La tasa de interés no puede ser menor o igual a 0');
    return false;
  }
  if (Number(creditDetails.arrearRate) <= 0 && !canArrearRateBeZero) {
    alert('La tasa de interés moratorio no puede ser menor o igual a 0');
    return false;
  }
  if (creditDetails.operationCost < 0) {
    alert('El costo de operación no puede ser menor a 0');
    return false;
  }
  if (creditDetails.otherExpenses < 0) {
    alert('Los gastos operacionales no pueden ser menor a 0');
    return false;
  }
  if (creditDetails.taxes < 0) {
    alert('Los impuestos no pueden ser menor a 0');
    return false;
  }
  if (creditDetails.notary < 0) {
    alert('La notaria no puede ser menor a 0');
    return false;
  }
  if (creditDetails.guaranteeAmount < 0) {
    alert('El monto en garantía no puede ser menor a 0');
    return false;
  }
  if (creditDetails.guaranteeAmount > 0) {
    if (creditDetails.guaranteeObservation === '') {
      alert('La observación de la garantía no puede ser vacía');
      return false;
    }
  }
  if (
    (creditDetails.ownerId === '' || !creditDetails.ownerId) &&
    (creditDetails.executive === '' || !creditDetails.executive)
  ) {
    alert('El ejecutivo no puede ser vacío');
    return false;
  }
  return true;
};

const CreateCreditDialog = (props: any) => {
  const dispatch = useDispatch();
  const {
    open,
    setOpen,
    setUp,
    setSuccessOpen,
    setFailureOpen,
    currentPage,
    currentPageSize,
  } = props;
  const [loading, setLoading] = useState(false);
  const [business, setBusiness] = useState<any>({});
  const [searched, setSearched] = useState(false);
  const operationDefaultDate = new Date();
  const firstPaymentDefaultDate = new Date();
  firstPaymentDefaultDate.setDate(firstPaymentDefaultDate.getDate() + 30);
  const [amortizationSchedule, setAmortizationSchedule] = useState<
    AmortizationRow[]
  >([]);

  const [isRateZeroViolated, setIsRateZeroViolated] = useState<
    boolean | undefined
  >(undefined);
  const [isArrearRateZeroViolated, setIsArrearRateZeroViolated] = useState<
    boolean | undefined
  >(undefined);
  const [canRateBeZero, setCanRateBeZero] = useState<boolean | undefined>(
    undefined,
  );
  const [canArrearRateBeZero, setCanArrearRateBeZero] = useState<
    boolean | undefined
  >(undefined);

  const [selectedRequest, setSelectedRequest] = useState<any>({
    businessIdentifier: '',
    requestDate: new Date().toISOString(),
  });

  const setDocumentsToApply = (docs: any[]) => {
    setCreditDetails((prev: any) => ({
      ...prev,
      documentsToApply: docs,
    }));
  };

  const [executivesPortfolio, setExecutivesPortfolio] = useState([]);
  const [executivesAll, setExecutivesAll] = useState([]);

  const [creditDetails, setCreditDetails] = useState<{
    amount: number;
    months: number;
    operationCost: number;
    otherExpenses: number;
    rate: number | string;
    anualRate?: number | string;
    arrearRate: number | string;
    anualArrearRate?: number | string;
    periodicity: 'MONTHLY' | 'QUARTERLY' | 'SEMI_ANNUAL' | 'ANNUAL';
    interestType: 'COMPOUND' | 'SIMPLE';
    type: 'WORKING_CAPITAL' | 'CONSUMER';
    operationDate: string;
    firstPaymentDate: string;
    warranties: any[];
    taxes: number;
    notary: number;
    guaranteeAmount: number;
    amountToApply: number;
    guaranteeObservation: string;
    documentsToApply: any[];
    executive: string;
    ownerId?: string;
  }>({
    amount: 0,
    months: 12,
    operationCost: 0,
    otherExpenses: 0,
    rate: 1,
    anualRate: 12,
    periodicity: 'MONTHLY',
    arrearRate: 0,
    anualArrearRate: 0,
    interestType: 'COMPOUND',
    type: 'WORKING_CAPITAL',
    operationDate: operationDefaultDate.toISOString().split('T')[0],
    firstPaymentDate: firstPaymentDefaultDate.toISOString().split('T')[0],
    warranties: [],
    taxes: 0,
    notary: 0,
    guaranteeAmount: 0,
    amountToApply: 0,
    guaranteeObservation: '',
    documentsToApply: [],
    executive: '',
  });

  useEffect(() => {
    if (business && searched) {
      setSelectedRequest({
        businessIdentifier: business.identifier,
        requestDate: new Date().toISOString(),
      });
    }
  }, [business, searched]);

  const fetchExecutives = async () => {
    if (business?.identifier) {
      try {
        const response = await getExecutivesAvailableForFactoring(
          dispatch,
          business.identifier,
        );
        setExecutivesPortfolio(response.usersWithBusinessInPortfolio);
        setExecutivesAll(response.allUsers);
      } catch (error) {
        console.error('Error fetching executives:', error);
      }
    }
  };

  useEffect(() => {
    void fetchConfigurationValues();
  }, []);

  useEffect(() => {
    void fetchExecutives();
  }, [business]);

  const fetchConfigurationValues = async () => {
    const configurations = await getCompanyConfigOld(
      dispatch,
      ['credit_request_rate_zero', 'credit_request_arrear_rate_zero'].join(','),
    );
    setIsRateZeroViolated(false);
    setIsArrearRateZeroViolated(
      configurations.credit_request_arrear_rate_zero === '0',
    );
    setCanRateBeZero(configurations.credit_request_rate_zero === '1');
    setCanArrearRateBeZero(
      configurations.credit_request_arrear_rate_zero === '1',
    );
  };

  const handleSubmit = async () => {
    if (
      !validateCreditRequestForm(
        creditDetails,
        canRateBeZero,
        canArrearRateBeZero,
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      await createCredit(dispatch, {
        amount: Number(creditDetails.amount),
        months: Number(creditDetails.months),
        type: creditDetails.type,
        interestType: creditDetails.interestType,
        operationCost: Number(creditDetails.operationCost),
        operationCostIVA: Math.round(creditDetails.operationCost * 0.19),
        otherExpenses: Number(creditDetails.otherExpenses),
        operationDate: new Date(creditDetails.operationDate),
        rate: Number(creditDetails.rate),
        arrearRate: Number(creditDetails.arrearRate),
        businessIdentifier: business.identifier,
        cuotas: amortizationSchedule,
        paymentDate:
          amortizationSchedule[amortizationSchedule.length - 1].paymentDate,
        warranties: creditDetails.warranties,
        periodicity: creditDetails.periodicity,
        taxes: Number(creditDetails.taxes),
        notary: Number(creditDetails.notary),
        guaranteeAmount: Number(creditDetails.guaranteeAmount),
        guaranteeObservation: creditDetails.guaranteeObservation,
        amountToApply: Number(creditDetails.amountToApply),
        documentsToApply: creditDetails.documentsToApply.map((doc: any) => {
          return {
            id: doc.id,
            amountToConciliate: doc.amountToConciliate,
          };
        }),
        ownerId: creditDetails.ownerId || creditDetails.executive,
      });
      setSuccessOpen('Crédito creado correctamente');
      await setUp({ page: 0, pageSize: 10 });
      setLoading(false);
      setOpen(false);
    } catch (error) {
      setLoading(false);
      setFailureOpen('Error al crear el crédito');
    }
  };

  // Update violation states when creditDetails change
  useEffect(() => {
    if (canRateBeZero !== undefined) {
      if (creditDetails.rate === 0 && !canRateBeZero) {
        setIsRateZeroViolated(true);
      } else {
        setIsRateZeroViolated(false);
      }
    }
  }, [creditDetails.rate, canRateBeZero]);

  useEffect(() => {
    if (canArrearRateBeZero !== undefined) {
      if (creditDetails.arrearRate === 0 && !canArrearRateBeZero) {
        setIsArrearRateZeroViolated(true);
      } else {
        setIsArrearRateZeroViolated(false);
      }
    }
  }, [creditDetails.arrearRate, canArrearRateBeZero]);

  const creditDetailsValid = () => {
    const amountValid = creditDetails.amount > 0;
    const monthsValid = creditDetails.months > 0;
    const rateValid = Number(creditDetails.rate) > 0 || !isRateZeroViolated;
    const arrearRateValid =
      Number(creditDetails.arrearRate) > 0 || !isArrearRateZeroViolated;
    const operationDateValid = creditDetails.operationDate !== '';
    const firstPaymentDateValid = creditDetails.firstPaymentDate !== '';

    const result =
      amountValid &&
      monthsValid &&
      rateValid &&
      arrearRateValid &&
      operationDateValid &&
      firstPaymentDateValid;

    return result;
  };

  return (
    <Dialog maxWidth="lg" open={open}>
      <CustomDialogTitle setOpen={setOpen}>Solicitar Crédito</CustomDialogTitle>
      {loading && (
        <DialogContent style={{ minWidth: '500px' }}>
          <CenteredCircularProgress type="dialog" />
        </DialogContent>
      )}
      {!loading && (
        <DialogContent style={{ minWidth: '500px' }}>
          {!searched && (
            <div>
              <CompanyBusinessSearcher
                setBusiness={setBusiness}
                setSearched={setSearched}
              />
            </div>
          )}
          {searched && (
            <div>
              <ClientInfo
                identifier={business?.identifier}
                businessName={business?.businessName}
              />
              <CreditSimulator
                creditDetails={creditDetails}
                setCreditDetails={setCreditDetails}
                amortizationSchedule={amortizationSchedule}
                setAmortizationSchedule={setAmortizationSchedule}
                editing={true}
                newCreditRequest={true}
                selectedRequest={selectedRequest}
                setDocumentsToApply={setDocumentsToApply}
                documentsToApply={creditDetails.documentsToApply}
                executivesPortfolio={executivesPortfolio}
                executivesAll={executivesAll}
              />
            </div>
          )}
        </DialogContent>
      )}
      <DialogActions>
        <CustomButton
          onClick={() => setOpen(false)}
          color="secondary"
          variant="contained"
          disabled={loading}
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading || !creditDetailsValid()}
        >
          Solicitar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCreditDialog;
